import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';

import { useTelemetry } from '@order/Telemetry';

import { useNavigateToCustomization, useNavigateToMenu } from '../../../hooks';
import { useProductForRestaurantQuery } from '../graphql/ProductForRestaurant.query.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationConfirm = (props: Params) => {
  const { restaurantId = '', restaurantSlug, deliveryAddressId, baseProductSlug } = props;

  const orderType = deliveryAddressId ? 'delivery' : 'pickup';

  const navigateToMenu = useNavigateToMenu();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Query ───────────────────────────────────────────────────────────

  const [productForRestaurantResponse] = useProductForRestaurantQuery({
    requestPolicy: 'cache-and-network',
    variables: { baseProductSlug, restaurantId },
    pause: !restaurantId,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasRestaurantData = Boolean(restaurantId);
  const productForRestaurantData = productForRestaurantResponse.data?.productForRestaurant;
  const productSlug = productForRestaurantData?.slug;

  const isFetchingProduct =
    productForRestaurantResponse.fetching || productForRestaurantResponse.stale;

  const isProductNotAvailable =
    hasRestaurantData && !isFetchingProduct && !productForRestaurantData;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToPDP = useNavigateToCustomization({
    restaurantSlug,
    deliveryAddressId,
  });

  const handleLocationConfirm = useCallback(async () => {
    if (isProductNotAvailable) {
      navigateToMenu({
        orderType,
        restaurantSlug,
        deliveryOrderDetail: {
          addressId: deliveryAddressId,
        },
      });

      setTimeout(() => {
        addNoticeBanner({
          text: formatMessage(messages.locationProductNotAvailable),
          palette: 'caution',
        });
      });

      return;
    }

    track('deep-linking.continue_pdp_flow');
    navigateToPDP({ slug: productSlug });
  }, [
    addNoticeBanner,
    deliveryAddressId,
    formatMessage,
    isProductNotAvailable,
    navigateToMenu,
    navigateToPDP,
    orderType,
    productSlug,
    restaurantSlug,
    track,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    handleLocationConfirm,
    isProductNotAvailable,
    isFetchingProduct,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationProductNotAvailable: {
    defaultMessage: `We're sorry, this item is no longer available.`,
    description: 'Location | Product Not Available',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = Readonly<{
  baseProductSlug: string;
  restaurantId?: string;
  restaurantSlug?: string;
  deliveryAddressId?: string;
  deliveryVendorName?: string;
  deliveryVendorRestaurantId?: string;
  deliveryFee?: number;
}>;
